/**
 * 网页开着时，提醒在页面里弹出来、响一声、念出来（2026-10-29 接上）。纯函数，不碰 DOM，有测试（inpage-alerts.test.ts）。
 *
 * 推到手机（Telegram / Bark / 飞书）那一路照旧，这里是**额外**的：他正好开着网站的时候，不用去看手机。
 * 哪一种提醒怎么弹由各自那张卡上的设置管：
 *   patience 极度耐心   pa_style（角落 / 居中 / 只念）、pa_duration 停几秒、pa_sound、pa_voice
 *   sitting  久坐       st_fs（大一点的遮挡）、st_sound、st_voice、st_break 建议活动几分钟、st_tips 随机一句提示
 *   creed    每日心法   cr_speak 念不念、cr_speakSource（主心法 cr_text / 播报库 cr_speakList 随机）
 *   reminder 事项提醒   rm_style（居中 / 角落）、rm_fullscreen、rm_sound、rm_voice
 * 念出来还要语音总开关 ge_voice 开着；音量 ge_vol、语速 ge_rate、音调 ge_vPitch、voiceFollow（按界面语言挑声音）在 lib/voice.ts。
 */

type Flat = Readonly<Record<string, unknown>>;

export type PopStyle = 'toast' | 'center' | 'full' | 'none';

export interface AlertPlan {
  style: PopStyle;
  /** 自动消失的毫秒数。0 = 等他点「知道了」 */
  ms: number;
  sound: boolean;
  /** 要念的那句话。null = 不念 */
  speak: string | null;
  /** 弹窗里正文下面多加的一句（久坐的建议、随机提示） */
  extra: string | null;
}

const bool = (s: Flat, k: string, d: boolean): boolean => (typeof s[k] === 'boolean' ? (s[k] as boolean) : d);
const num = (s: Flat, k: string, d: number, lo: number, hi: number): number => {
  const v = s[k];
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
};
const lines = (v: unknown): string[] => (typeof v === 'string' ? v.split('\n').map(x => x.trim()).filter(Boolean) : []);

/** 从一串里随机挑一句。`rnd` 注入进来是为了测试 */
export function pickLine(v: unknown, rnd: () => number = Math.random): string | null {
  const l = lines(v);
  return l.length === 0 ? null : l[Math.floor(rnd() * l.length) % l.length]!;
}

/**
 * 有自己那张卡上的弹窗设置的几种（样式、停留、声音、语音）。
 * 2026-10-30 起**别的提醒也弹**（熔断、利润锁死、价格……，老板：网页不弹窗吗）：最紧急的居中，其余角落停 12 秒
 */
export const INPAGE_KINDS: ReadonlySet<string> = new Set(['patience', 'sitting', 'creed', 'reminder']);

/** 「测试提醒效果」按下去：网页马上照那一种的样子弹（detail 是 NotifyTestRead.preview） */
export const TEST_ALERT_EVENT = 'jyfk:test-alert';

export function planAlert(kind: string, title: string, body: string, s: Flat, lang: 'zh-CN' | 'zh-HK' | 'en' = 'zh-CN', rnd: () => number = Math.random, urgency = 'active'): AlertPlan | null {
  const voiceOn = bool(s, 'ge_voice', true);
  const say = (on: boolean, text: string): string | null => (on && voiceOn && text.trim() !== '' ? text.trim().slice(0, 240) : null);
  switch (kind) {
    case 'patience': {
      const st = s['pa_style'] === 'center' ? 'center' : s['pa_style'] === 'voiceOnly' ? 'none' : 'toast';
      return {
        style: st, ms: num(s, 'pa_duration', 12, 3, 120) * 1000,
        sound: bool(s, 'pa_sound', false), speak: say(bool(s, 'pa_voice', false) || st === 'none', body || title), extra: null
      };
    }
    case 'sitting': {
      const brk = num(s, 'st_break', 5, 1, 120);
      const tip = pickLine(s['st_tips'], rnd);
      return {
        style: bool(s, 'st_fs', false) ? 'full' : 'center', ms: 0,
        sound: bool(s, 'st_sound', true), speak: say(bool(s, 'st_voice', false), title),
        extra: `${lang === 'en' ? `Take a ${brk}-minute break.` : lang === 'zh-HK' ? `建議起來活動 ${brk} 分鐘。` : `建议起来活动 ${brk} 分钟。`} ${tip ?? ''}`.trim()
      };
    }
    case 'creed': {
      const text = s['cr_speakSource'] === 'list' ? (pickLine(s['cr_speakList'], rnd) ?? '') : (lines(s['cr_text']).join('，') || body);
      return { style: 'center', ms: 0, sound: false, speak: say(bool(s, 'cr_speak', false), text), extra: null };
    }
    case 'reminder':
      return {
        style: bool(s, 'rm_fullscreen', false) ? 'full' : s['rm_style'] === 'toast' ? 'toast' : 'center', ms: 0,
        sound: bool(s, 'rm_sound', true), speak: say(bool(s, 'rm_voice', true), title), extra: null
      };
    default:
      /* 其余的提醒（熔断、利润锁死、价格、链热度……）：最紧急的居中等他点「知道了」，其余角落停 12 秒。
         利润锁死跟着它那张卡的「提示音」；不念（和扩展弹的系统通知同一个规矩，见 apps/api 的 alert-sound.ts） */
      return {
        style: urgency === 'critical' ? 'center' : 'toast', ms: urgency === 'critical' ? 0 : 12_000,
        sound: kind === 'profitLock' || kind === 'profitLockOverdue' ? bool(s, 'pl_sound', true) : true, speak: null, extra: null
      };
  }
}
